import { useEffect, useRef, useCallback, useMemo } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { PaymentType } from "@/stores/slices/commonSlice";
import { usePayment } from "@/stores/appStore";

export function usePaymentParams() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  // 读取参数
  const open = useMemo(() => searchParams.get('open') === 'true', [searchParams]);
  const type = useMemo(() => (searchParams.get('type') as PaymentType), [searchParams]);
  const price = useMemo(() => {
    const val = searchParams.get('price');
    return val ? parseFloat(val) : 0;
  }, [searchParams]);

  // store
  const {
    paymentOpen,
    paymentType,
    getPaymentPrice,
    openPayment,
  } = usePayment();
  const paymentPrice = getPaymentPrice();

  // 防止死循环
  const initialized = useRef(false);

  // 1. 首次加载时，URL → store
  useEffect(() => {
    if (!initialized.current) {
      openPayment({
        open,
        type,
        price,
      })
      initialized.current = true;
    }
    // eslint-disable-next-line
  }, []);

  // 2. store 变化时，store → URL
  useEffect(() => {
    if (initialized.current) {
      const newParams = new URLSearchParams(searchParams.toString());
      if (typeof paymentOpen === 'boolean') {
        newParams.set('open', String(paymentOpen));
      } else {
        newParams.delete('open');
      }
      if (paymentType && ['upgrade', 'recharge'].includes(paymentType)) {
        newParams.set('type', paymentType);
      } else {
        newParams.delete('type');
      }
      if (typeof paymentPrice === 'number') {
        newParams.set('price', paymentPrice.toString());
      } else {
        newParams.delete('price');
      }
      router.replace(`${pathname}?${newParams.toString()}`);
    }
  }, [paymentOpen, paymentType, paymentPrice, searchParams, pathname, router]);

  // 3. 提供 setPaymentParams 方法
  const setPaymentParams = useCallback(
    (params: Partial<{ open: boolean; type: PaymentType; price: number }>) => {
      openPayment(params);
    },
    [openPayment]
  );

  return { open: paymentOpen, type: paymentType, price: paymentPrice, setPaymentParams };
} 